import React from 'react';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { FormField } from '../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../global/default-datepicker/default-datepicker';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import OptionsButton from '../../../../global/options-button/options-button';
import { PresentersSection } from '../../shared/presenters-list/presenters-list-container';
import { ConnectionsList } from '../../shared/connections-list/connections-list.presentation';
import { hasPermission, isSuperUser } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { OperatorsSection } from '../../shared/operators-list/operators-list-container';
import { ConnectionDropdownPresentation } from '../../shared/_connection-dropdown/connection-dropdown.presentation';
import { TimeInput } from '../../../../global/time-input/time-input';
import { RoomFormVM } from './room-form.viewmodel';
import { OpenFrom } from '../../../../../tracking-service/tracking-values';
import {
    cancelText,
    END_DATE_TEXT,
    END_TIME_TEXT,
    MORE_BUTTON_TEXT,
    NAME_TEXT,
    START_DATE_TEXT,
    START_TIME_TEXT,
    submitText
} from '../../../../../constants/common-labels.constants';
import { HOST_CONNECTION_TEXT, ROOM_DATA_IS_BEING_SAVED_TEXT, ROOM_DATA_IS_LOADING_TEXT, ROOM_LABEL_TEXT, V2_CONNECTIONS_TEXT } from './room-form.texts';
import { OEToggle } from '../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../global/text/text';
import { ConnectionVersion, EnablementStatus } from '../../../../../constants/common.constants';
import { ConnectionGroupsList } from '../../shared/connection-groups-list/connection-groups-list';

interface IProps {
    vm: RoomFormVM;
    intl: IntlShape;
}

const RoomFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;
    const formData = vm.formDataVM;
    const operators = vm.eventOperators;
    const eventTimezone = vm.getEvent().timeZone;

    const startDate = formData.startDateTime ? new Date(formData.startDateTime) : null;
    const endDate = formData.endDateTime ? new Date(formData.endDateTime) : null;

    const derivedConnection = vm.defaultConnectionId
        ? vm.connections.concat(vm.connectionGroups).find(({ meetingConnectionId }) => meetingConnectionId === vm.defaultConnectionId)
        : null;

    const selectedPresenters = vm.presentersVM?.getPresenters() ?? [];
    const roomPresenters = ((selectedPresenters.length) ? ' - ' + selectedPresenters.map(p => p.name).join(', ') : '');
    const event = vm.getEvent();
    const meetingTopic = `${event.eventTitleForPassport || event.name}${roomPresenters}`;

    const roomOptions = vm.isEditRoom() && hasPermission(Permission.DELETE_ROOM)
        ? [{ label: 'Delete Room', id: 'oe.room.deleteRoom', action: props.vm.confirmDeletingRoom, danger: true }]
        : [];

    const dateFormat = vm.getDateFormat;
    const eventDates = vm.getEventDatesArray();
    const shouldShowConnectionVersion = hasPermission(Permission.CREATE_NEW_ROOM_V2) && !vm.isEditRoom();

    return (
        <CommonPopupBodyFooterLayout
            body={(
                <form className='oe-form room-form'>
                    <div className='oe-form__body'>
                        {shouldShowConnectionVersion && <FormField className='m-bottom-20px'>
                            <div className='oe-form__row'>
                                <OEToggle
                                    isSelected={formData.getConnectionVersion()}
                                    onClick={() => formData.setConnectionVersion(!formData.getConnectionVersion())}
                                    isDisabled={!isSuperUser()}
                                />
                                <Text
                                    {...V2_CONNECTIONS_TEXT}
                                    values={{
                                        suffix: formData.getConnectionVersion() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                                    }}
                                />
                            </div>
                        </FormField>}
                        {formData.name && <FormField label={<FormattedMessage {...NAME_TEXT} />}>{formData.name}</FormField>}
                        <FormField label={<FormattedMessage {...ROOM_LABEL_TEXT} />}>
                            <input
                                type='text'
                                className='oe-form-field__control'
                                value={formData.label}
                                onChange={mobx.action(e => formData.label = e.target.value)}
                            />
                            {formData.isFormChecked() &&
                                <span className='oe-form-field__error'>{formData.getRoomLabelError()}</span>}
                        </FormField>
                        <div className='event_date_field m-bottom-25px'>
                            <div className='oe-form__row'>
                                <FormField
                                    label={<FormattedMessage {...START_DATE_TEXT} />}
                                >
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.startDate}
                                        onChange={date => formData.setStartDate(date)}
                                        showTimeSelect={false}
                                        dateformat={dateFormat}
                                        highlightDates={eventDates}
                                    />
                                    {formData.isFormChecked() &&
                                        <span className='oe-form-field__error'>{formData.getStartDateError()}</span>}
                                </FormField>
                                <FormField label={<FormattedMessage {...END_DATE_TEXT} />}>
                                    <DefaultDatePicker
                                        className='oe-form-field__control'
                                        selected={formData.endDate}
                                        onChange={date => formData.setEndDate(date)}
                                        popperPlacement='bottom-end'
                                        showTimeSelect={false}
                                        dateformat={dateFormat}
                                        highlightDates={eventDates}
                                    />
                                    {formData.isFormChecked() &&
                                        <span className='oe-form-field__error'>{formData.getEndDateError()}</span>}
                                </FormField>
                            </div>
                            {formData.isFormChecked() && <span className='oe-form-field__error'>{formData.getDatesError()}</span>}
                        </div>
                        <div className='event_time_field m-bottom-25px'>
                            <div className='oe-form__row'>
                                <FormField
                                    label={<FormattedMessage {...START_TIME_TEXT} />}
                                >
                                    <TimeInput
                                        className='oe-form-field__control'
                                        date={formData.startDateTime}
                                        disabled={!formData.startDate}
                                        onChange={date => formData.setStartTime(date)}
                                        onError={mobx.action(() => formData.startTimeValidationError = true)}
                                    />
                                    {formData.isFormChecked() &&
                                        <span className='oe-form-field__error'>{formData.getStartTimeError()}</span>}
                                </FormField>
                                <FormField label={<FormattedMessage {...END_TIME_TEXT} />}>
                                    <TimeInput
                                        className='oe-form-field__control'
                                        date={formData.endDateTime}
                                        disabled={!formData.endDate}
                                        onChange={date => formData.setEndTime(date)}
                                        onError={mobx.action(() => formData.endTimeValidationError = true)}
                                    />
                                    {formData.isFormChecked() &&
                                        <span className='oe-form-field__error'>{formData.getEndTimeError()}</span>}
                                </FormField>
                            </div>
                            {formData.isFormChecked() && <span className='oe-form-field__error'>{formData.getTimesError()}</span>}
                        </div>
                        {vm.isEditRoom() && <ConnectionsList
                            startDate={startDate}
                            endDate={endDate}
                            timeZone={eventTimezone}
                            topic={meetingTopic}
                            meetingApiParams={vm.roomApiParams}
                            connectionTypes={vm.connectionTypes}
                            connections={vm.getSortedConnectionList()}
                            defaultConnectionId={vm.defaultConnectionId}
                            updateDefaultConnection={vm.updateDefaultConnection}
                            createConnection={vm.createRoomConnection}
                            updateConnection={vm.editRoomConnection}
                            deleteConnection={vm.deleteRoomConnection}
                            intl={intl}
                            event={vm.event}
                            getKnovioConnectionDetails={vm.getKnovioRoomConnectionDetails}
                            openFrom={OpenFrom.Rooms}
                            isKnovioReplayConnectionExist={null}
                            handleTroubleshoot={vm.handleTroubleshoot}
                            showConfigurePassportForm={vm.showConfigurePassportForm}
                            trackHostJoinClick={vm.trackHostJoinClick}
                            presenterOrganizationList={selectedPresenters}
                        />}
                        {vm.isEditRoom() && formData.getParsedConnectionVersion() === ConnectionVersion.V2 &&
                            <ConnectionGroupsList
                                getGroups={vm.getConnectionGroupList.bind(vm)}
                                getConnections={vm.getSortedConnectionList.bind(vm)}
                                contextParams={{ roomId: vm.roomApiParams.roomId }}
                                createConnectionGroup={vm.createRoomConnectionGroup}
                                getDefaultConnectionId={() => vm.defaultConnectionId}
                                updateDefaultConnection={vm.updateDefaultConnection}
                                editConnectionGroup={vm.editRoomConnectionGroup}
                                deleteConnectionGroup={vm.deleteRoomConnectionGroup}
                            />
                        }
                        {
                            vm.presentersVM &&
                            <PresentersSection
                                showConnectionsDropdown={vm.isEditRoom()}
                                vm={vm.presentersVM}
                                eventPresenters={vm.eventPresenters}
                                derivedConnection={derivedConnection}
                                connections={vm.connections}
                                connectionGroups={vm.connectionGroups}
                            />
                        }

                        {vm.isEditRoom() && <section className='presenters-section'>
                            <div className='presenters-section__header'>
                                <label className='presenters-section__header-label'>
                                    <FormattedMessage {...HOST_CONNECTION_TEXT} />
                                </label>
                                {
                                    <ConnectionDropdownPresentation
                                        meetingConnections={vm.connections}
                                        connectionGroups={vm.connectionGroups}
                                        assignedConnectionId={formData.hostRoleConnectionId}
                                        isLabelVisibleCondition={!!(vm.connections.length && formData.hostRoleConnectionId)}
                                        onChange={mobx.action((connectionId: number | null): void => formData.setHostRoleConnectionId(connectionId))}
                                    />
                                }
                            </div>
                        </section>}
                        {formData && (
                            <OperatorsSection
                                vm={formData}
                                operators={operators}
                                selectOperators={props.vm.selectOperators.bind(props.vm)}
                                searchOperators={props.vm.searchOperators.bind(props.vm)}
                            />
                        )
                        }
                    </div>
                </form>
            )}
            footer={(
                <>
                    {props.vm.isSaving() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage {...ROOM_DATA_IS_BEING_SAVED_TEXT} />
                        </span>
                    )}
                    {!props.vm.getIsFormDataReady() && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage {...ROOM_DATA_IS_LOADING_TEXT} />
                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(vm.isEditRoom())}
                            modifier={['fat', 'confirm-safe']}
                            onClick={props.vm.formSubmitAction}
                            disabled={props.vm.isSaving()}
                        />
                        <DefaultCloseSubWindowButton text={cancelText()} />
                        {!!roomOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={roomOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            )}
        />
    );
}));

export { RoomFormPresentation };
